import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { installExternalLinkInterceptor } from '@/lib/utils/externalLinks'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { TelemetryBridge } from '@/lib/telemetry'
import MarketingLayout from '@/core/app/layout/MarketingLayout'
import AppLayout from '@/core/app/layout/AppLayout'
import { AuthGate } from '@/core/auth/AuthGate'
import { ForgotPasswordForm } from '@/core/auth/views/ForgotPasswordForm'
import { ResetPasswordForm } from '@/core/auth/views/ResetPasswordForm'
import { LoginPage } from '@/core/auth/views/LoginPage'
import { RegisterPage } from '@/core/auth/views/RegisterPage'
import { OAuthCompletePage } from '@/core/auth/views/OAuthCompletePage'
import { RequireAuth } from '@/core/auth/RequireAuth'
import { RedirectIfAuthed } from '@/core/auth/RedirectIfAuthed'
import { RequireSuperAdmin } from '@/core/auth/RequireSuperAdmin'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'
import { getRegisteredModules } from '@/core/registry'

// Phase 19-08 — embed surface (UE-embedded WebView routes, dashboard,
// modules, bridge) lives in src/embed/ and is reachable only through
// dist/embed.html. The website tree intentionally has zero embed imports.

// Lazy-loaded marketing pages (code-split — separate chunk from auth-gated routes)
// Phase 10 Plan 10-01 (MKT-01) — homepage is now public marketing surface.
const HomePage = lazy(() => import('@/core/marketing/pages/HomePage'))
const AboutPage = lazy(() => import('@/core/marketing/pages/AboutPage'))
const NewsPage = lazy(() => import('@/core/marketing/pages/NewsPage'))
const NewsArticlePage = lazy(
  () => import('@/core/marketing/pages/NewsArticlePage'),
)
const NewsArchivePage = lazy(
  () => import('@/core/marketing/pages/NewsArchivePage'),
)
const RoadmapPage = lazy(() => import('@/core/marketing/pages/RoadmapPage'))
const ChangelogPage = lazy(
  () => import('@/core/marketing/pages/ChangelogPage'),
)
// Public docs surface backed by cms_docs (Phase 7) — single component
// handles both /docs (index) and /docs/:slug (article view) so the
// sidebar persists across navigations.
const DocsPage = lazy(() => import('@/core/marketing/pages/DocsPage'))
// Plan 08-05 (PROD-02) — public CMS-driven pages. /pricing and /modules
// each fetch a single `cms_product_pages` row by hard slug and dispatch
// the body_blocks JSONB through BlockRenderer. SSG follows in Plan 08-06.
const FaqsPage = lazy(() => import('@/core/marketing/pages/FaqsPage'))
const ModulesPage = lazy(() => import('@/core/marketing/pages/ModulesPage'))
const ModuleDetailPage = lazy(
  () => import('@/core/marketing/pages/ModuleDetailPage'),
)
// Plan 10-02 (MKT-03 + MKT-06) — static marketing pages. /features is a
// static module overview (NOT www's community feature-request board, which
// is out of scope per PROJECT.md). /partners is a 1:1 port of www's
// partner-program landing page.
const FeaturesPage = lazy(() => import('@/core/marketing/pages/FeaturesPage'))
const PartnersPage = lazy(() => import('@/core/marketing/pages/PartnersPage'))
// Plan 10-03 (MKT-07 + MKT-08) — interactive-ish marketing pages.
// /contact is a RHF+Zod port of www's contact form, server-backed via
// POST /api/contact with Cloudflare Turnstile verification (see
// server/src/server/handlers/platform/contact.rs + useContactSubmit.ts).
// /support is a 1:1 port of www's support hub (Discord + Contact Form
// cards + secondary docs/FAQ links).
const ContactPage = lazy(() => import('@/core/marketing/pages/ContactPage'))
const SupportPage = lazy(() => import('@/core/marketing/pages/SupportPage'))
// Plan 10-04 (MKT-09) — Legal pages. Verbatim ports of www's privacy /
// terms / impressum, code-split to keep the marketing chunk size down.
// Impressum emits robots="noindex, nofollow" via Path B inline Helmet.
const PrivacyPage = lazy(() => import('@/core/marketing/pages/PrivacyPage'))
const TermsPage = lazy(() => import('@/core/marketing/pages/TermsPage'))
const ImpressumPage = lazy(() => import('@/core/marketing/pages/ImpressumPage'))
// Plan 10-05 (MKT-10) — Updates aggregator. CMS-driven feed merging
// news + changelog client-side via useUpdatesFeed. No new sidecar
// endpoint — the merge happens in the React hook layer; both source
// queries fail-open (one source erroring still renders surviving items).
const UpdatesPage = lazy(() => import('@/core/marketing/pages/UpdatesPage'))

// Lazy-loaded account pages (code-split)
const AccountPage = lazy(() => import('@/core/app/account/AccountPage'))
const SettingsPage = lazy(() => import('@/core/app/account/SettingsPage'))

// Plan 12-05 (USR-02) — /admin/* routes retired from the SPA. The four
// legacy admin page files at src/core/app/admin/* STAY ON DISK until
// Phase 15 (CUT-01) deletes the legacy admin tree alongside the
// admin.gamedevbuddy.com / www.gamedevbuddy.com directory cleanup.
// Redirect routes for /admin/* are registered below (preserving :id for
// the user-detail redirect via RedirectAdminUserDetail).
// openresty-redirects.md spec rows authored for Phase 15 production-side 301s.

// Lazy-loaded checkout pages (code-split). Phase 17 Plan 17-06 retired
// the legacy browse surface; CheckoutSuccessPage stays as the post-Stripe
// landing page (reached via Plan 17-05's onComplete callback).
const CheckoutSuccessPage = lazy(() => import('@/core/app/store/CheckoutSuccessPage'))
// One-click Discord linking target. PUBLIC so an emailed / DM'd link works
// while signed out — it bounces through /login?next=/link-discord itself.
const LinkDiscordPage = lazy(() => import('@/core/app/account/LinkDiscordPage'))

// Phase 17 Plan 17-02 — Embedded checkout shell at /checkout/:moduleSlug.
// Single canonical purchase URL replacing the hosted-Stripe-redirect flow.
// Plans 17-03 / 17-04 / 17-05 filled in the right-column panels (inline
// auth / tier picker / Stripe Embedded Checkout mount).
const CheckoutPage = lazy(() => import('@/core/marketing/checkout/CheckoutPage'))

// Lazy-loaded console — super-admin only, distinct chunk (CON-03 / Plan 03-03).
// vite.config.ts manualChunks groups all src/core/console/* into the 'console'
// chunk so marketing visitors (loading /about, /, /modules, /login) never
// download admin code on first paint.
const ConsoleLayout = lazy(() => import('@/core/console/layout/ConsoleLayout'))

// Plan 03-04 (CON-04) — generic CRUD pages. Driven by collectionSchemas[slug];
// every Phase 4-9 slice reuses these — zero per-slice page code.
const CollectionListPage = lazy(
  () => import('@/core/console/pages/CollectionListPage'),
)
const DocumentEditPage = lazy(
  () => import('@/core/console/pages/DocumentEditPage'),
)
// Plan 03-08 (MED-01) — media library full-page view at /console/media. Sits
// next to the generic :collection routes (the sidebar already links here per
// ConsoleSidebar's nav table).
const MediaLibraryPage = lazy(
  () => import('@/core/console/pages/MediaLibraryPage'),
)
// Plan 03-09 (CON-07) — /console index dashboard with 4 placeholder tiles
// (Recent Activity, Quick Actions, Pending LaunchKits, Open GitLab Issues).
// Real data wires in Phase 13 (GitLab) + Phase 14 (LaunchKits). Aliased
// as `ConsoleDashboardPage` because the embed-routes modules-grid already
// claims the `DashboardPage` name at module scope (line 8).
const ConsoleDashboardPage = lazy(
  () => import('@/core/console/pages/DashboardPage'),
)
// Plan 12-01 (USR-01) — port of /admin/users + /admin/users/:id into the
// console. Sidebar's Users nav item already points at /console/users
// (Phase 3 locked). The /admin/* route tree below stays mounted until
// Plan 12-05 retires it; both surfaces coexist for one phase.
const UsersPage = lazy(() => import('@/core/console/pages/UsersPage'))
const UserDetailPage = lazy(
  () => import('@/core/console/pages/UserDetailPage'),
)
// Plan 12-03 (STRP-01) — Subscriptions dashboard at /console/subscriptions.
// MRR + active count + failed count + recent-events feed. Each section
// owns its own loading/error/empty state — partial backend failure does
// not white-screen the whole page (per PLAN 12-03 success criterion).
const SubscriptionsPage = lazy(
  () => import('@/core/console/pages/SubscriptionsPage'),
)
// Plan 13-04 (GL-01) — GitLab issue board at /console/gitlab. Mounts the
// Plan 13-03 components (KanbanBoard + sidebars + IssueModal/Panel) inside
// a FilterProvider with 4 UI states (loading / empty / error / disabled).
// ConsoleSidebar's GitLab nav item already points here (Phase 3 line 96).
const GitLabPage = lazy(() =>
  import('@/core/console/pages/GitLabPage').then((m) => ({
    default: m.GitLabPage,
  })),
)
// Plan 14-03 (DSC-01) — Discord webhook console. Lazy-loaded behind the
// same RequireSuperAdmin gate as /console/gitlab; ConsoleSidebar's Discord
// nav item already points here (Phase 3 line 93). Mirrors GitLabPage's
// named-export-via-lazy wiring exactly.
const DiscordPage = lazy(() =>
  import('@/core/console/pages/DiscordPage').then((m) => ({
    default: m.DiscordPage,
  })),
)
// Plan 14-05 (TWT-01) — X / Twitter console. Lazy-loaded behind the
// same RequireSuperAdmin gate as /console/discord; mirrors DiscordPage's
// named-export-via-lazy wiring exactly. Composer uses MediaPicker
// (Phase 3 / Plan 03-08) for image attachments via OAuth1.0a upload.
const TwitterPage = lazy(() =>
  import('@/core/console/pages/TwitterPage').then((m) => ({
    default: m.TwitterPage,
  })),
)
// Plan 14-07 (YT-01) — YouTube console. Lazy-loaded behind the same
// RequireSuperAdmin gate as /console/twitter; mirrors TwitterPage's
// named-export-via-lazy wiring. VideoList + MetadataEditor modal for
// editing title / description / tags / privacyStatus on uploaded videos.
const YouTubePage = lazy(() =>
  import('@/core/console/pages/YouTubePage').then((m) => ({
    default: m.YouTubePage,
  })),
)
// Plan 14-09 (LK-01..03) — LaunchKit list + edit pages. Same lazy pattern;
// `/console/launch-kit` lists all kits status-bucket grouped, `:id` opens
// the 4-section edit page (Metadata + 3 ChannelSection + SendAllPanel
// right-rail). ConsoleSidebar's "Launch Kit" nav item already points at
// /console/launch-kit (Phase 3 / Plan 03-02).
const LaunchKitListPage = lazy(() =>
  import('@/core/console/launch-kits/LaunchKitListPage').then((m) => ({
    default: m.LaunchKitListPage,
  })),
)
// Stub /console/settings page — sidebar link existed without a route, which
// silently fell through to CollectionListPage's :collection catch and
// redirected to /console. Real platform settings page is a later phase.
const ConsoleSettingsPage = lazy(
  () => import('@/core/console/pages/SettingsPage'),
)
const LaunchKitEditPage = lazy(() =>
  import('@/core/console/launch-kits/LaunchKitEditPage').then((m) => ({
    default: m.LaunchKitEditPage,
  })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
    },
  },
})

// Plan 12-05 (USR-02) — preserve :id when redirecting /admin/users/:id →
// /console/users/:id. <Navigate to="/console/users/:id"> would treat ":id"
// as a literal string segment, so we read the param and build the
// destination URL inline. Sibling redirects (/admin/users → /console/users
// etc.) use plain <Navigate> because they have no path params to preserve.
function RedirectAdminUserDetail() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/console/users/${id ?? ''}`} replace />
}

function App() {
  // 2026-09-02 (Craig): external links must NEVER navigate away in-tab —
  // one capture-phase interceptor covers every anchor, present and future
  // (CMS bodyHtml included), instead of per-site target="_blank" discipline.
  // Browser app only; the UE-embed App (src/embed/App.tsx) is untouched.
  useEffect(() => installExternalLinkInterceptor(), [])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* V2 sync (Theme 10, from 7cc48382) — installs window.onerror +
            unhandledrejection forwarders and the tester telemetry-notice
            toast. Returns null; mounting it inside the provider tree so
            useAuth() resolves. Idempotent on re-mount. */}
        <TelemetryBridge />
        <Routes>
          {/* ============================================================
              Browser routes — standalone app with full chrome.
              Embed routes (/embed/*) live in src/embed/App.tsx, served
              from dist/embed.html — see Phase 19-08.
              ============================================================ */}

          {/* Platform layout: minimal authenticated app shell (logo + nav).
              Routes split into three groups by auth requirement — see #113. */}
          <Route element={<MarketingLayout />}>
            {/* Public marketing pages — no auth gate */}

            {/* Phase 10 Plan 10-01 (MKT-01) — / is now the public marketing
                homepage. Logged-in visitors who want the dashboard navigate to
                /account explicitly via the topbar nav; the previous
                RequireAuth-gated /→/account redirect is removed. /account
                stays auth-gated below. */}
            <Route
              path="/"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <HomePage />
                </Suspense>
              }
            />

            <Route
              path="/about"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <AboutPage />
                </Suspense>
              }
            />

            {/* Plan 04-04 (NEWS-03) — public list of published news articles */}
            <Route
              path="/news"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <NewsPage />
                </Suspense>
              }
            />

            {/* Full blog archive — the WordPress-style listing that /news's
                "View All Articles" links to. MUST be registered BEFORE
                `/news/:slug` so the literal "archive" segment isn't captured
                as an article slug. */}
            <Route
              path="/news/archive"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <NewsArchivePage />
                </Suspense>
              }
            />

            {/* Plan 04-05 (NEWS-04 + NEWS-05) — public article detail page.
                MUST be registered AFTER `/news` so the literal segment wins
                even though RR prefers literals over params at the same depth.
                The slug is normalised lowercase by the server; mismatched
                casing returns 404 → NotFound panel. */}
            <Route
              path="/news/:slug"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <NewsArticlePage />
                </Suspense>
              }
            />

            {/* Plan 05-03 (ROAD-02) — public roadmap, status-bucket grouped
                (Now / Next / Later). 'done' items hidden by default; module
                filter via chip group. No /roadmap/:slug detail page (out of
                scope per CONTEXT.md decision lock). */}
            <Route
              path="/roadmap"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <RoadmapPage />
                </Suspense>
              }
            />

            {/* Plan 06-03 (CHG-02) — public changelog list, version anchors
                via <article id={slug}>. No /changelog/:slug detail page
                (out of scope per CONTEXT.md decision lock). */}
            <Route
              path="/changelog"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ChangelogPage />
                </Suspense>
              }
            />

            {/* Public docs — backed by cms_docs (Phase 7). One component
                handles both routes so the sticky sidebar persists. This
                in-SPA path is the canonical docs surface; the standalone
                Docusaurus subdomain was retired 2026-05-11. */}
            <Route
              path="/docs"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <DocsPage />
                </Suspense>
              }
            />
            <Route
              path="/docs/:slug"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <DocsPage />
                </Suspense>
              }
            />

            {/* /pricing → /faqs restructure (2026-05-12): per-product
                pricing now lives on the module detail page (CC + gdb-suite
                rich-tier dispatch in ModuleDetailPage). /faqs keeps the
                accordion + Studio enterprise card. No redirect from
                /pricing — the site isn't published yet, nothing's
                bookmarked. */}
            <Route
              path="/faqs"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <FaqsPage />
                </Suspense>
              }
            />
            <Route
              path="/modules"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ModulesPage />
                </Suspense>
              }
            />
            {/* Per-module detail page driven by cms_products + GET
                /api/products/:slug. Renders the long-form `description` HTML
                + gallery + features + pricing + checkout CTA. */}
            <Route
              path="/modules/:slug"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ModuleDetailPage />
                </Suspense>
              }
            />

            {/* Plan 10-02 (MKT-03) — static module overview. NOT www's
                community feature-request board (out of scope per
                PROJECT.md "Out of Scope: Public voting on roadmap").
                Suggestions go through GitLab via the Phase 13 /roadmap
                form. */}
            <Route
              path="/features"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <FeaturesPage />
                </Suspense>
              }
            />

            {/* Plan 10-02 (MKT-06) — partner program landing page,
                1:1 port from www. Apply Now CTAs link out to the
                external app subdomain until the Phase 13+ partner-apply
                form lands inside the SPA. */}
            <Route
              path="/partners"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <PartnersPage />
                </Suspense>
              }
            />

            {/* Plan 10-03 (MKT-07) — contact form. Server-backed via
                POST /api/contact with Cloudflare Turnstile verification.
                Submitter's email becomes the message Reply-To so support
                can reply directly. */}
            <Route
              path="/contact"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ContactPage />
                </Suspense>
              }
            />

            {/* Plan 10-03 (MKT-08) — support hub. Discord (recommended)
                + Contact Form cards + secondary "Browse Documentation /
                View FAQ" CTAs. /docs resolves to the in-SPA DocsPage
                (backed by cms_docs); /docs/faq currently falls through
                to the catch-all redirect. */}
            <Route
              path="/support"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <SupportPage />
                </Suspense>
              }
            />

            {/* Plan 10-04 (MKT-09) — Legal pages. Verbatim ports of www's
                privacy / terms / impressum (no copy edits — phase preamble
                locks legal content). Privacy + Terms use the default
                RouteHead `index, follow`. Impressum uses Path B inline
                `<Helmet>` to override RouteHead's default with
                `noindex, nofollow` per the German § 5 TMG metadata. */}
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <PrivacyPage />
                </Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <TermsPage />
                </Suspense>
              }
            />
            <Route
              path="/impressum"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ImpressumPage />
                </Suspense>
              }
            />

            {/* Plan 10-05 (MKT-10) — Updates aggregator surface. Merges
                news + changelog into a single chronological feed via the
                useUpdatesFeed hook (client-side merge, no new sidecar
                endpoint). One-source-failure resilient: if news API is
                down but changelog succeeds, the feed still renders the
                surviving items. */}
            <Route
              path="/updates"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <UpdatesPage />
                </Suspense>
              }
            />

            {/* Phase 17 Plan 17-06 — the legacy /store route was deleted.
                /modules is the canonical browse/buy surface; each Buy CTA
                deep-links to /checkout/:moduleSlug below. The catch-all
                Navigate at the bottom of this Routes tree handles any
                stale bookmarks (→ "/"). */}

            {/* Phase 17 Plan 17-02 — Embedded checkout. Single canonical
                /checkout/:moduleSlug URL accessible from any surface
                (/modules Buy Now, www Buy CTAs, in-app deep links). Inline
                auth + tier picker + Stripe Embedded Checkout mount, all on
                this single route. */}
            <Route
              path="/checkout/:moduleSlug"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <CheckoutPage />
                </Suspense>
              }
            />

            {/* /sign-in is NOT a page — it is an alias kept alive for EMAIL that has
                already been sent. Six transactional CTAs ("Sign In to Your Account",
                "Log In to Download") linked {site_url}/sign-in, which no route ever
                matched, so the catch-all dropped customers on the homepage. The links
                now say /login, but messages sent before that fix will keep arriving in
                inboxes for years and must still work.
                Masked today by the path-dropping apex redirect; the domain cutover
                unmasks it. */}
            <Route path="/sign-in" element={<Navigate to="/login" replace />} />

            {/* Auth pages — redirect to /account when already logged in (#113) */}
            <Route element={<RedirectIfAuthed />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            </Route>

            {/* OAuth success landing — PUBLIC, and deliberately NOT wrapped in
                RedirectIfAuthed. The backend 302s here with the JWT in the URL
                fragment (#token=…) after a social sign-in; this page persists
                the token + refreshes auth, then sends the user to /account.
                Gating it on "not already authed" would race the token handoff,
                so it stays a bare public route. OAuth FAILURES never reach
                here — the backend redirects those straight to
                /login?oauth_error=<reason>. */}
            <Route path="/auth/oauth/complete" element={<OAuthCompletePage />} />

            {/* Password reset — PUBLIC, and deliberately NOT wrapped in
                RedirectIfAuthed. The backend emails this link to new buyers
                (set-your-password) AND forgot-password users; a logged-out new
                buyer MUST be able to reach it to set their first password.
                Even if somehow authed, the user came here specifically to set a
                password — bouncing them to /account would strand the flow — so
                it stays a bare public route. The page reads ?token=… and
                validates it against the cloud before showing the form. */}
            <Route path="/reset-password" element={<ResetPasswordForm />} />

            {/* Post-Stripe checkout success — PUBLIC. A GUEST buyer's account
                is auto-created by the webhook but has no password/session yet,
                so this page MUST be reachable without auth (it previously sat
                under RequireAuth, which bounced guests to a bare /login with no
                confirmation). CheckoutSuccessPage branches on auth state:
                authed buyers see "modules activated" + dashboard CTA; guests
                see the "we emailed a set-password link" confirmation. */}
            <Route
              path="/checkout/success"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <CheckoutSuccessPage />
                </Suspense>
              }
            />

            {/* One-click Discord linking — PUBLIC. Every "link your Discord"
                prompt points here: the checkout success page, the purchase
                email, the bot's /link command and its DM to unlinked joiners.
                Public because those arrive from outside the app and the user
                may be signed out; the page redirects itself through
                /login?next=/link-discord and resumes. */}
            <Route
              path="/link-discord"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <LinkDiscordPage />
                </Suspense>
              }
            />

            {/* Auth-required — typing the URL while logged out → /login (#113).
                Phase 10 Plan 10-01 (MKT-01): the previous /→/account redirect
                was removed. `/` now serves the public marketing homepage
                (above, under MarketingLayout); /account stays auth-gated and
                is reachable via direct URL or the topbar nav. */}
            <Route element={<RequireAuth />}>
              <Route
                path="/account"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <AccountPage />
                  </Suspense>
                }
              />
              <Route
                path="/settings"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              {/* Plan 12-05 (USR-02) — legacy /admin/* paths redirect to
                  /console/* equivalents. Files at src/core/app/admin/*
                  STAY ON DISK until Phase 15 (CUT-01) deletes the legacy
                  admin tree. openresty-side 301s for the same paths are
                  documented in .planning/specs/openresty-redirects.md and
                  ship in Phase 15 alongside the marketing-domain cutover.
                  Order matters: literal segments first, /admin/* catch-all
                  last (React Router prefers literals over wildcards but
                  explicit ordering removes ambiguity). */}
              <Route path="/admin/users/:id" element={<RedirectAdminUserDetail />} />
              <Route path="/admin/users" element={<Navigate to="/console/users" replace />} />
              {/* Phase 16 Plan 08: /admin/products specific redirect deleted —
                  the /admin/* catch-all below redirects to /console which now
                  hosts the products page at /console/products (Plan 16-03). */}
              <Route path="/admin/*" element={<Navigate to="/console" replace />} />
            </Route>
          </Route>

          {/* App layout: with sidebar, used for module workspaces -- auth gated */}
          <Route element={<AuthGate />}>
            <Route element={<AppLayout />}>
              {getRegisteredModules()
                .filter((m) => m.routes.length > 0)
                .flatMap((m) =>
                  m.routes.map((r) => (
                    <Route
                      key={r.path}
                      path={r.path}
                      element={
                        <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                          {r.element}
                        </Suspense>
                      }
                    />
                  ))
                )}
            </Route>
          </Route>

          {/* ============================================================
              Console — super-admin only. Lazy-chunked (Plan 03-03);
              gated by RequireSuperAdmin (Plan 03-01). Sibling subtree
              to MarketingLayout: zero shared chrome, no marketing
              imports — vite manualChunks isolates this into its own
              `console` chunk so marketing visitors never download it.
              ============================================================ */}
          <Route element={<RequireSuperAdmin />}>
            <Route
              path="/console"
              element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                  <ConsoleLayout />
                </Suspense>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <ConsoleDashboardPage />
                  </Suspense>
                }
              />
              {/* Plan 03-08 (MED-01) — media library MUST be registered
                  BEFORE the dynamic `:collection` route. React Router prefers
                  literal segments over params at the same depth, but listing
                  it explicitly first removes any ambiguity. */}
              <Route
                path="media"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <MediaLibraryPage />
                  </Suspense>
                }
              />
              {/* Plan 12-01 (USR-01) — Users list + detail. Registered as
                  literal segments BEFORE the dynamic `:collection` route
                  for the same router-precedence reason as `media`. The old
                  /admin/users + /admin/users/:id routes (above) stay mounted
                  until Plan 12-05; both surfaces coexist for one phase. */}
              <Route
                path="users"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <UsersPage />
                  </Suspense>
                }
              />
              <Route
                path="users/:id"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <UserDetailPage />
                  </Suspense>
                }
              />
              {/* Plan 13-04 (GL-01) — GitLab issue board. Literal segment
                  registered BEFORE the dynamic `:collection` route, ordered
                  alphabetically between `users` and `subscriptions` to match
                  the ConsoleSidebar's section visual order (Integrations
                  before Admin). The GitLab nav item at sidebar line 96
                  already points here. */}
              <Route
                path="gitlab"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <GitLabPage />
                  </Suspense>
                }
              />
              {/* Plan 14-03 (DSC-01) — Discord webhook console. Literal
                  segment registered BEFORE the dynamic `:collection` route
                  for the same router-precedence reason as `gitlab` and
                  `users`. ConsoleSidebar's Discord nav item already points
                  here (Phase 3 line 93, Integrations section). */}
              <Route
                path="discord"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <DiscordPage />
                  </Suspense>
                }
              />
              {/* Plan 14-05 (TWT-01) — X / Twitter console. Literal segment
                  registered BEFORE the dynamic `:collection` route for the
                  same router-precedence reason as `discord` and `gitlab`. */}
              <Route
                path="twitter"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <TwitterPage />
                  </Suspense>
                }
              />
              {/* Plan 14-07 (YT-01) — YouTube console. Literal segment
                  registered BEFORE the dynamic `:collection` route for the
                  same router-precedence reason as `twitter` / `discord`. */}
              <Route
                path="youtube"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <YouTubePage />
                  </Suspense>
                }
              />
              {/* Plan 12-03 (STRP-01) — Subscriptions dashboard. Literal
                  segment registered BEFORE the dynamic `:collection` route
                  for the same router-precedence reason as `users` and
                  `media`. ConsoleSidebar's "Subscriptions" nav item already
                  points here (Phase 3 locked). */}
              <Route
                path="subscriptions"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <SubscriptionsPage />
                  </Suspense>
                }
              />
              {/* Plan 14-09 (LK-01..03) — LaunchKit list + edit. Both literal
                  segments registered BEFORE the dynamic `:collection` route
                  for the same router-precedence reason as `discord` /
                  `twitter` / `youtube`. ConsoleSidebar's "Launch Kit" nav
                  item points at /console/launch-kit (Phase 3 / Plan 03-02
                  line 79). */}
              <Route
                path="launch-kit"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <LaunchKitListPage />
                  </Suspense>
                }
              />
              <Route
                path="launch-kit/:id"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <LaunchKitEditPage />
                  </Suspense>
                }
              />
              {/* Settings — placeholder until a Phase delivers the real
                  platform-config surface. Registered BEFORE the dynamic
                  :collection route for the same router-precedence reason
                  as the other literal segments in this block. */}
              <Route
                path="settings"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <ConsoleSettingsPage />
                  </Suspense>
                }
              />
              {/* Plan 03-04 (CON-04) — generic CRUD child routes. The
                  :collection param matches any slug; CollectionListPage and
                  DocumentEditPage fail closed (Navigate to /console) for
                  slugs not registered in collectionSchemas. Phase 4-9 slices
                  light up by adding entries to collectionSchemas — no new
                  routes needed here. */}
              <Route
                path=":collection"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <CollectionListPage />
                  </Suspense>
                }
              />
              <Route
                path=":collection/new"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <DocumentEditPage />
                  </Suspense>
                }
              />
              <Route
                path=":collection/:id"
                element={
                  <Suspense fallback={<div className="flex items-center justify-center h-64"><LoadingSpinner /></div>}>
                    <DocumentEditPage />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
